import { useCallback, useEffect, useRef, type ReactNode } from 'react'

import { RecordReachProvider, type RecordReachLookup } from '@/shared/access'
import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'

import { parseOrganizationSummary } from './organization'
import { organizationRoute, organizationsApi } from './paths'

/**
 * Links an organization's name only for a caller who may open it: Find Organizations, asked for the
 * linked ids, answers with those in their organization read reach, as Organization Details does.
 */
export function OrganizationReachProvider(props: { readonly children: ReactNode }) {
  const client = useApiClient()
  // Kept current without changing the lookup, which would ask again: the client changes when sign-in settles.
  const api = useRef(client)
  useEffect(() => {
    api.current = client
  }, [client])

  const lookup = useCallback<RecordReachLookup>(async (ids) => {
    const query = new URLSearchParams({ ids: ids.join(','), limit: String(ids.length) })
    const found = parseCursorPage(await api.current.get(`${organizationsApi}?${query.toString()}`), parseOrganizationSummary)
    return new Set(found.data.map((organization) => organization.id))
  }, [])

  return (
    <RecordReachProvider route={organizationRoute} lookup={lookup}>
      {props.children}
    </RecordReachProvider>
  )
}
